import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";

const categories = [
  {
    icon: "ri-body-scan-line",
    title: "Orthopedic and Pain Services",
    tone: "primary",
    items: [
      "Orthopedic consultations",
      "Osteoarthritis-related care",
      "Intra-articular injections",
      "Platelet-rich plasma procedures",
      "Trigger-point therapy",
      "Pain treatment support",
    ],
  },
  {
    icon: "ri-microscope-line",
    title: "Diagnostic Services",
    tone: "accent",
    items: ["Laboratory examinations", "X-rays", "Sonograms", "Electrocardiograms"],
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Wellness and Support Services",
    tone: "secondary",
    items: ["IV vitamin therapy", "Medically supervised weight-management support"],
  },
];

const toneMap: Record<string, { bubble: string; bar: string; check: string }> = {
  primary: { bubble: "bg-primary-100 text-primary-700", bar: "bg-primary-500", check: "text-primary-500" },
  accent: { bubble: "bg-accent-100 text-accent-700", bar: "bg-accent-500", check: "text-accent-500" },
  secondary: {
    bubble: "bg-secondary-100 text-secondary-700",
    bar: "bg-secondary-500",
    check: "text-secondary-500",
  },
};

export default function ServicesCategories() {
  return (
    <section className="w-full bg-background-100 border-y border-background-200 px-4 md:px-6 py-16 md:py-24">
      <div className="max-w-content mx-auto">
        <Reveal>
          <SectionHeading
            eyebrow="Medical services"
            title="Our Medical Services"
            description="Our services are organized into three categories. Each service is offered in an outpatient setting and discussed with patients based on their individual situation."
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            const tone = toneMap[category.tone];
            return (
              <Reveal key={category.title} delay={index * 100}>
                <article className="h-full flex flex-col rounded-xl border border-background-200 bg-background-50 overflow-hidden">
                  <div className={`h-1 w-full ${tone.bar}`} />
                  <div className="p-6 flex flex-col flex-1">
                    <span className={`w-12 h-12 rounded-lg flex items-center justify-center ${tone.bubble}`}>
                      <i className={`${category.icon} text-2xl`} />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                      {category.title}
                    </h3>
                    <ul className="mt-5 space-y-3 flex-1">
                      {category.items.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <i className={`ri-checkbox-circle-line mt-0.5 ${tone.check}`} />
                          <span className="text-sm text-foreground-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <p className="mt-8 text-xs leading-relaxed text-foreground-500 max-w-3xl">
            The services listed above describe what the clinic offers. No service is described as a
            cure, and nothing on this page should be read as medical advice or a guarantee of any
            particular result. Suitability of any service is determined by the clinic.
          </p>
        </Reveal>
      </div>
    </section>
  );
}